import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { Form, Formik } from 'formik';
import axios from 'axios';
import clsx from 'clsx';
import { withStyles } from '@material-ui/core/styles';
import { Box, Button, Container, IconButton, TextField, Typography } from '@material-ui/core';
import { Clear, Publish } from '@material-ui/icons';
import { FormDataSerializer } from '@ridecell/web-common';

import styles from './styles';
import { Create } from '@material-ui/icons';
import useGlobalContext from '../../../common/hooks/useGlobalContext';
import messages from './messages';

const CompanyInfoTab = props => {
  const {
    classes,
    intl: { formatMessage },
    isCarsharing,
  } = props;
  const [isEdit, setIsEdit] = useState(false);
  const {
    state: { associatedBusinesses, activeBusinessId },
    setAssociatedBusinesses,
  } = useGlobalContext();
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    if (
      associatedBusinesses &&
      associatedBusinesses.find(business => business.id === activeBusinessId)
    ) {
      const {
        brand_logo,
        contact_address,
        invoice_email,
        legal_name,
        tax_identifier,
      } = associatedBusinesses.find(business => business.id === activeBusinessId);
      setFormValues({
        brand_logo,
        legal_name,
        contact_address,
        invoice_email,
        tax_identifier,
      });
    }
  }, [associatedBusinesses, activeBusinessId]);

  const updateCompanyInfo = values => {
    const serializer = new FormDataSerializer();
    const serializedValues = serializer.serialize(values);

    // If brand logo is not remove or added then do not send for patch
    if (values.brand_logo !== '' && typeof values.brand_logo !== 'object')
      serializedValues.delete('brand_logo');

    axios.patch(`/api/v3/business_entity/${activeBusinessId}`, serializedValues).then(() => {
      axios.get('/api/v3/business_entity').then(({ data }) => {
        if (data.results.length > 0) setAssociatedBusinesses(data.results);
        setFormValues(serializedValues);
        setIsEdit(false);
      });
    });
  };

  const cancelUpdate = resetForm => {
    setFormValues(formValues);
    resetForm();
    setIsEdit(false);
  };

  const validateInfo = values => {
    const errors = {};
    Object.entries(values).forEach(entry => {
      if (!entry[1] && entry[0] === 'legal_name')
        errors[entry[0]] = formatMessage(messages.required);
    });

    return errors;
  };

  const displayLogo = image => {
    if (image instanceof File) return URL.createObjectURL(image);
    return image;
  };

  return (
    <Formik
      enableReinitialize
      initialValues={formValues}
      onSubmit={updateCompanyInfo}
      validate={validateInfo}
      validateOnChange={false}
    >
      {({ errors, values, handleChange, resetForm, setFieldValue }) => (
        <Form>
          <Container>
            <Box className={clsx(classes.flexAndSpaceBetween, classes.companyInfoTitle)}>
              <Box className={classes.flexAndSpaceBetween}>
                <input
                  accept="image/*"
                  className={classes.input}
                  hidden
                  id="brand-logo"
                  onChange={event => {
                    const file = event.currentTarget.files[0];
                    file && setFieldValue('brand_logo', file);
                  }}
                  type="file"
                />
                {values.brand_logo ? (
                  <React.Fragment>
                    <img
                      alt="brand_logo"
                      className={isEdit ? classes.logoBorder : classes.hiddenBorder}
                      height={60}
                      src={displayLogo(values.brand_logo)}
                    />
                    {isEdit && (
                      <IconButton className={classes.clearLogoButtonContainer}>
                        <Clear
                          className={classes.clearLogoButton}
                          onClick={() => setFieldValue('brand_logo', '')}
                        />
                      </IconButton>
                    )}
                  </React.Fragment>
                ) : (
                  <Box className={clsx(classes.noLogoContainer, classes.logoBorder)}>
                    <Typography className={classes.logoPlaceholder}>
                      {formatMessage(messages.logo)}
                    </Typography>
                  </Box>
                )}
                {isEdit && (
                  <React.Fragment>
                    <label htmlFor="brand-logo">
                      <Button
                        className={classes.uploadButton}
                        color="primary"
                        component="span"
                        startIcon={<Publish className={classes.publishIcon} />}
                        variant="outlined"
                      >
                        {formatMessage(messages.upload)}
                      </Button>
                    </label>
                  </React.Fragment>
                )}
              </Box>
              <IconButton
                className={classes.pencilIconContainer}
                onClick={() => {
                  if (!isEdit === false) cancelUpdate(resetForm);
                  setIsEdit(!isEdit);
                }}
              >
                <Create className={classes.pencilIcon} />
              </IconButton>
            </Box>
            <Box className={classes.flexAndSpaceBetween}>
              <Typography className={classes.companyInfoHeader}>
                {formatMessage(messages.legalCompanyName)}
              </Typography>
              <TextField
                className={classes.companyInfoFields}
                disabled={isCarsharing || !isEdit}
                error={errors.legal_name}
                helperText={errors.legal_name}
                name="legal_name"
                onChange={handleChange}
                type="text"
                value={values['legal_name']}
              />
            </Box>
            <Box className={classes.flexAndSpaceBetween}>
              <Typography className={classes.companyInfoHeader}>
                {formatMessage(messages.primaryContactEmail)}
              </Typography>
              <TextField
                className={classes.companyInfoFields}
                disabled={!isEdit}
                error={errors.invoice_email}
                helperText={errors.invoice_email}
                name="invoice_email"
                type="email"
                onChange={handleChange}
                value={values['invoice_email']}
              />
            </Box>
            <Box className={classes.flexAndSpaceBetween}>
              <Typography className={classes.companyInfoHeader}>
                {formatMessage(messages.taxIdentifier)}
              </Typography>
              <TextField
                className={classes.companyInfoFields}
                disabled={!isEdit}
                error={errors.tax_identifier}
                helperText={errors.tax_identifier}
                name="tax_identifier"
                type="tel"
                onChange={handleChange}
                value={values['tax_identifier']}
              />
            </Box>
            <Box className={classes.flexAndSpaceBetween}>
              <Typography className={classes.companyInfoHeader}>
                {formatMessage(messages.corporateAddress)}
              </Typography>
              <TextField
                className={classes.companyInfoFields}
                disabled={!isEdit}
                error={errors.contact_address}
                helperText={errors.contact_address}
                name="contact_address"
                type="text"
                onChange={handleChange}
                value={values['contact_address']}
              />
            </Box>
            {isEdit && (
              <div className={classes.companyInfoButtons}>
                <Button
                  style={{ marginRight: 20 }}
                  onClick={() => cancelUpdate(resetForm)}
                  color="primary"
                  variant="outlined"
                >
                  {formatMessage(messages.cancel)}
                </Button>
                <Button style={{ width: 150 }} color="primary" type="submit" variant="contained">
                  {formatMessage(messages.saveInfo)}
                </Button>
              </div>
            )}
          </Container>
        </Form>
      )}
    </Formik>
  );
};

export default withStyles(styles)(injectIntl(CompanyInfoTab));
